export interface RegisteredUser {
	id: string;
	email: string;
	registrationStatus: "OnBoarding" | "Completed";
}

export interface AuthStrategy {
	readonly type: string;
	readonly name: string;
	readonly description?: string;

	// Core authentication methods
	authenticate: (
		credentials: unknown,
		options?: Record<string, unknown>,
	) => Promise<AuthResult>;
	logout?: () => Promise<void>;

	// Optional methods for different auth flows
	refresh?: () => Promise<AuthResult>;
	validate?: () => Promise<boolean>;

	// Configuration and capabilities
	isAvailable: () => boolean;
	getRequiredFields: () => string[];
	supportsFeature: (feature: AuthFeature) => boolean;
}

export interface AuthResult {
	success: boolean;
	user?: RegisteredUser;
	token?: string;
	refreshToken?: string;
	error?: string;
	details?: Record<string, unknown>;
}

export enum AuthFeature {
	PASSWORD_RESET = "password_reset",
	EMAIL_VERIFICATION = "email_verification",
	TWO_FACTOR = "two_factor",
	SOCIAL_LOGIN = "social_login",
	MAGIC_LINK = "magic_link",
	BIOMETRIC = "biometric",
}

export interface AuthConfig {
	strategies: AuthStrategy[];
	defaultStrategy?: string;
	fallbackStrategy?: string;
	enabledFeatures?: AuthFeature[];
	sessionTimeout?: number;
	storage?: "localStorage" | "sessionStorage" | "memory";
}

// Specific credential types for different auth strategies
export interface EmailPasswordCredentials {
	email: string;
	password: string;
}

export interface MagicLinkCredentials {
	email: string;
	returnUrl?: string;
}

export interface OAuthCredentials {
	provider: string;
	code?: string;
	state?: string;
}

export interface VerificationTokenCredentials {
	token: string;
	identifier: string;
}

export interface KeycloakCredentials {
	username?: string;
	password?: string;
	// For authorization code flow, no credentials are needed as it's handled by redirect
}

export type AuthCredentials =
	| EmailPasswordCredentials
	| MagicLinkCredentials
	| OAuthCredentials
	| VerificationTokenCredentials
	| KeycloakCredentials
	| Record<string, unknown>;
